import { supabase } from '../lib/supabase';

export interface FrameProduct {
  subcategoryId: number;
  name: string;
  minimumWidth: string;
  maximumWidth: string;
  minimumHeight: string;
  maximumHeight: string;
  requiredDPI: number;
}

export interface FrameAvailability {
  subcategoryId: number;
  isAvailable: boolean;
  frameInfo?: FrameProduct;
}

export interface FrameAvailabilityResponse {
  success: boolean;
  frameAvailability: Record<string, FrameAvailability>;
  allProducts: FrameProduct[];
  lastUpdated: string;
  error?: string;
}

class FrameAvailabilityService {
  private cache: FrameAvailabilityResponse | null = null;
  private cacheExpiry: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Fetch frame availability from Lumaprints API via our edge function
   */
  async getFrameAvailability(forceRefresh = false): Promise<FrameAvailabilityResponse> {
    const now = Date.now();
    
    // Return cached data if it's still fresh and not forcing refresh
    if (!forceRefresh && this.cache && now < this.cacheExpiry) {
      console.log('🔄 Using cached frame availability data');
      return this.cache;
    }

    try {
      console.log('🖼️ Fetching fresh frame availability data...');
      
      const { data, error } = await supabase.functions.invoke('get-frame-products', {
        body: {}
      });

      if (error) {
        console.error('❌ Error calling frame products function:', error);
        throw new Error(`Failed to fetch frame availability: ${error.message}`);
      }

      if (!data.success) {
        console.error('❌ Frame products function returned error:', data.error);
        throw new Error(data.error || 'Unknown error from frame products service');
      }

      console.log('✅ Successfully fetched frame availability');
      
      // Cache the response
      this.cache = data;
      this.cacheExpiry = now + this.CACHE_DURATION;
      
      return data;

    } catch (error) {
      console.error('❌ Failed to fetch frame availability:', error);
      
      // Return cached data if available as fallback
      if (this.cache) {
        console.log('⚠️ Using stale cached data as fallback');
        return this.cache;
      }
      
      // Return default availability (all frames available) if no cache
      console.log('⚠️ No cache available, returning default availability');
      return this.getDefaultAvailability();
    }
  }

  /**
   * Get availability for a specific frame
   */
  async isFrameAvailable(frameId: string, forceRefresh = false): Promise<boolean> {
    try {
      const availability = await this.getFrameAvailability(forceRefresh);
      return availability.frameAvailability[frameId]?.isAvailable ?? true; // Default to available
    } catch (error) {
      console.error(`❌ Error checking availability for frame ${frameId}:`, error);
      return true; // Default to available on error
    }
  }

  /**
   * Get available frame IDs
   */
  async getAvailableFrameIds(forceRefresh = false): Promise<string[]> {
    try {
      const availability = await this.getFrameAvailability(forceRefresh);
      return Object.entries(availability.frameAvailability)
        .filter(([_, frameData]) => frameData.isAvailable)
        .map(([frameId, _]) => frameId);
    } catch (error) {
      console.error('❌ Error getting available frame IDs:', error);
      return ['black', 'white', 'gold', 'natural', 'mocca']; // Default to all frames
    }
  }

  /**
   * Clear cache and force refresh on next request
   */
  clearCache(): void {
    this.cache = null;
    this.cacheExpiry = 0;
    console.log('🗑️ Frame availability cache cleared');
  }

  /**
   * Default availability when API is unavailable (all frames available)
   */
  private getDefaultAvailability(): FrameAvailabilityResponse {
    const defaultFrames = ['black', 'white', 'gold', 'natural', 'mocca'];
    const frameAvailability: Record<string, FrameAvailability> = {};
    
    defaultFrames.forEach(frameId => {
      frameAvailability[frameId] = {
        subcategoryId: this.getSubcategoryId(frameId),
        isAvailable: true
      };
    });

    return {
      success: true,
      frameAvailability,
      allProducts: [],
      lastUpdated: new Date().toISOString(),
      error: 'Using default availability - API unavailable'
    };
  }

  private getSubcategoryId(frameId: string): number {
    const map: Record<string, number> = {
      'black': 105001,
      'white': 105002,
      'gold': 105011,
      'natural': 105008,
      'mocca': 105012
    };
    return map[frameId] || 105001;
  }
}

// Export singleton instance
export const frameAvailabilityService = new FrameAvailabilityService();
